import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useConnections, type ConnectionSummary } from "@/api/queries";

/**
 * The source you were last working with (backlog items 2 and 7).
 *
 * One answer, shared by the sidebar's New conversation and by the workbench,
 * because a person has one working context and not one per page. It is a
 * preference rather than page state — whoever asked about Surf Shop last is,
 * on the balance of evidence, about to query Surf Shop — so it is persisted.
 *
 * Alphabetical-first is the wrong default: the first source in the list is
 * whichever one happens to sort first, which is how a first query lands on
 * Surf Conditions and fails. It stays only as the last resort, for a
 * deployment where nobody has chosen anything yet.
 */

interface PreferredSourceState {
  connectionId: string;
  setConnectionId: (connectionId: string) => void;
}

const useStore = create<PreferredSourceState>()(
  persist(
    (set) => ({
      connectionId: "",
      setConnectionId: (connectionId) => set({ connectionId }),
    }),
    { name: "ming.source" },
  ),
);

export interface PreferredSource {
  sources: ConnectionSummary[];
  /** Where you came from, then where you were, then whatever exists. */
  connectionId: string;
  select: (connectionId: string) => void;
}

/**
 * Where you came from, then where you were, then whatever exists.
 *
 * Pure and exported so the priority can be tested without a browser: it has
 * four branches and the interesting ones are the two where an id names a
 * source that has since been deleted.
 */
export function resolveSource(
  sources: Array<{ id: string }>,
  from?: string | null,
  stored?: string | null,
): string {
  const known = (id: string | null | undefined) =>
    Boolean(id) && sources.some((source) => source.id === id);
  if (known(from)) return from as string;
  if (known(stored)) return stored as string;
  return sources[0]?.id ?? "";
}

/**
 * @param from A source the caller already knows the reader means — a route's
 *   `?connection=`, or the source of the thread they have open. It wins over
 *   the remembered one, and is ignored if it names a source that is gone.
 */
export function usePreferredSource(from?: string | null): PreferredSource {
  const { data: connections } = useConnections();
  const stored = useStore((state) => state.connectionId);
  const select = useStore((state) => state.setConnectionId);
  const sources = connections ?? [];
  return { sources, connectionId: resolveSource(sources, from, stored), select };
}
